import { toast } from 'react-toastify';
import { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { 
    getPendingApprovals,
    approveBooking,
    rejectBooking
} from '@/services/marketingService';
import * as Sentry from '@sentry/react';

const NoChargeApprovalsPage = () => {
    const [approvals, setApprovals] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [showRejectModal, setShowRejectModal] = useState(false);
    const [selectedApproval, setSelectedApproval] = useState(null);
    const [rejectionReason, setRejectionReason] = useState('');

    const { apiFetch } = useAuth();

    useEffect(() => {
        fetchPendingApprovals();
    }, []);

    const fetchPendingApprovals = async () => {
        setIsLoading(true);
        try {
            const data = await getPendingApprovals(apiFetch);
            setApprovals(data);
        } catch (error) {
            Sentry.captureException(error);
            toast.error(error.message || 'Failed to load pending approvals');
        } finally {
            setIsLoading(false);
        }
    };

    const handleApprove = async (approval) => {
        if (!window.confirm(`Are you sure you want to approve this ${approval.bookingType} booking?`)) {
            return;
        }

        setIsLoading(true);
        try {
            await approveBooking(apiFetch, approval.bookingId, approval.bookingType);
            toast.success('Booking approved successfully');
            fetchPendingApprovals();
        } catch (error) {
            Sentry.captureException(error);
            toast.error(error.message || 'Failed to approve booking');
        } finally {
            setIsLoading(false);
        }
    };

    const handleRejectClick = (approval) => {
        setSelectedApproval(approval);
        setRejectionReason('');
        setShowRejectModal(true);
    };

    const handleReject = async () => {
        if (!rejectionReason.trim()) {
            toast.error('Please provide a reason for rejection');
            return;
        }

        setIsLoading(true);
        try {
            await rejectBooking(
                apiFetch, 
                selectedApproval.bookingId, 
                selectedApproval.bookingType, 
                rejectionReason
            );
            toast.success('Booking rejected successfully');
            setShowRejectModal(false);
            setSelectedApproval(null);
            setRejectionReason('');
            fetchPendingApprovals();
        } catch (error) {
            Sentry.captureException(error);
            toast.error(error.message || 'Failed to reject booking');
        } finally {
            setIsLoading(false);
        }
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString();
    };

    const getBookingTypeBadge = (type) => {
        return type === 'Media' ? 'badge-info' : 'badge-warning';
    };

    return (
        <div className="csi-container">
            <h1>No Charge Booking Approvals</h1>

            <div className="info-banner" style={{ 
                padding: '15px', 
                marginBottom: '20px', 
                backgroundColor: '#f0f0f0', 
                borderRadius: '5px',
                border: '1px solid #ccc'
            }}>
                <p style={{ margin: 0 }}>
                    <strong>ℹ️ Note:</strong> Bookings with "No Charge" payment status require manual approval 
                    before they can be confirmed. Review the details below and either approve or reject each booking.
                </p>
            </div>

            {isLoading ? (
                <div className="loading">Loading pending approvals...</div>
            ) : approvals.length === 0 ? (
                <div className="no-data" style={{ 
                    textAlign: 'center', 
                    padding: '50px', 
                    fontSize: '18px',
                    color: '#666'
                }}>
                    <p>✅ No pending approvals at this time.</p>
                </div>
            ) : (
                <div className="table-container">
                    <table className="data-table">
                        <thead>
                            <tr>
                                <th>Type</th>
                                <th>Space/Area</th>
                                <th>Counterparty</th>
                                <th>Property</th>
                                <th>Start Date</th>
                                <th>End Date</th>
                                <th>Comments</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {approvals.map((approval, index) => (
                                <tr key={`${approval.bookingType}-${approval.bookingId}-${index}`}>
                                    <td>
                                        <span className={`badge ${getBookingTypeBadge(approval.bookingType)}`}>
                                            {approval.bookingType}
                                        </span>
                                    </td>
                                    <td>{approval.spaceName}</td>
                                    <td>{approval.counterpartyName}</td>
                                    <td>{approval.propertyName}</td>
                                    <td>{formatDate(approval.startDate)}</td>
                                    <td>{formatDate(approval.endDate)}</td>
                                    <td>{approval.comments || '-'}</td>
                                    <td>
                                        <button 
                                            className="btn btn-sm btn-success"
                                            onClick={() => handleApprove(approval)}
                                            disabled={isLoading}
                                            style={{ marginRight: '5px' }}
                                        >
                                            ✓ Approve
                                        </button>
                                        <button 
                                            className="btn btn-sm btn-danger"
                                            onClick={() => handleRejectClick(approval)}
                                            disabled={isLoading}
                                        >
                                            ✗ Reject
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {showRejectModal && (
                <div className="modal-overlay">
                    <div className="modal-container">
                        <div className="modal-header">
                            <h3>Reject Booking</h3>
                        </div>
                        <div className="modal-body">
                            <p>
                                <strong>Booking Type:</strong> {selectedApproval?.bookingType}<br />
                                <strong>Space/Area:</strong> {selectedApproval?.spaceName}<br />
                                <strong>Counterparty:</strong> {selectedApproval?.counterpartyName}
                            </p>
                            <div className="form-group">
                                <label htmlFor="rejectionReason">Reason for Rejection *</label>
                                <textarea
                                    id="rejectionReason"
                                    value={rejectionReason}
                                    onChange={(e) => setRejectionReason(e.target.value)}
                                    className="form-control"
                                    rows="4"
                                    placeholder="Please provide a detailed reason for rejecting this booking..."
                                    required
                                />
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button 
                                className="btn btn-secondary" 
                                onClick={() => {
                                    setShowRejectModal(false);
                                    setSelectedApproval(null);
                                    setRejectionReason('');
                                }}
                                disabled={isLoading}
                            >
                                Cancel
                            </button>
                            <button 
                                className="btn btn-danger" 
                                onClick={handleReject}
                                disabled={isLoading || !rejectionReason.trim()}
                            >
                                {isLoading ? 'Rejecting...' : 'Reject Booking'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
</div>
    );
};

export default NoChargeApprovalsPage;
